import { defineStore, acceptHMRUpdate } from 'pinia';
import keyBy from 'lodash/keyBy';
import get from 'lodash/get';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import { useOrganizationStore } from '@/stores/organization.store';
import {
    fetchSubEssentialContacts,
    fetchOrgEssentialContacts,
    createOrUpdateSubEssentialContact,
    createOrUpdateOrgEssentialContact,
} from '@/stores/api/essential-contacts.api';

interface EssentialContact {
    id: string;
    type: 'sub' | 'org';
    emailContacts?: string[];
    [key: string]: unknown;
}

interface EssentialContactsState {
    error: unknown;
    fetching: boolean;
    map: Record<string, EssentialContact>;
}

function getInitialState (): EssentialContactsState {
    return {
        error: null,
        fetching: false,
        map: {},
    };
}

export const useEssentialContactsStore = defineStore('essentialContacts', {
    state: getInitialState,
    getters: {
        list: (state): EssentialContact[] => values(state.map),
    },
    actions: {
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (!isEmpty(this.map) && !reFetch) {
                return;
            }

            this.fetching = true;

            try {
                const subContacts = await fetchSubEssentialContacts();
                subContacts.forEach((contact: EssentialContact) => {
                    contact.type = 'sub';
                });

                let orgContacts: EssentialContact[] = [];
                const orgId = get(useOrganizationStore().getActive, 'id', null);

                if (orgId) {
                    orgContacts = await fetchOrgEssentialContacts(orgId);
                    orgContacts.forEach((contact: EssentialContact) => {
                        contact.type = 'org';
                    });
                }
                this.map = keyBy(
                    [...subContacts, ...orgContacts],
                    ({ type, id }: EssentialContact) => `${type}_${id}`,
                );
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.fetching = false;
            }
        },
        async createOrUpdateSub (contact: EssentialContact) {
            this.fetching = true;

            try {
                const updatedContact = await createOrUpdateSubEssentialContact(contact);
                const key = `sub_${updatedContact.id}`;
                this.map[key] = Object.freeze({ ...updatedContact, type: 'sub' });
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.fetching = false;
            }
        },
        async createOrUpdateOrg (contact: EssentialContact) {
            this.fetching = true;
            try {
                const activeOrganization = useOrganizationStore().getActive;
                const orgId = get(activeOrganization, 'id', null);
                const updatedContact = await createOrUpdateOrgEssentialContact(orgId, contact);
                const key = `org_${updatedContact.id}`;
                this.map[key] = Object.freeze({ ...updatedContact, type: 'org' });
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.fetching = false;
            }
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useEssentialContactsStore, module.hot));
}
